import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CropField } from '@next/arable-lib/crop-field';
import { Store } from '@ngrx/store';
import { combineLatest, Subject, switchMap, take, takeUntil, tap } from 'rxjs';

import { SiteState } from 'src/app/shared/models';

import { TranslatePipe } from '@next/core-lib/i18n';
import { LoadingCardComponent } from '../../../shared/components/loading-card.component/loading-card.component';
// eslint-disable-next-line max-len
import { SpecificFormHeaderSectionComponent } from '../../components/questionnaire/form-header-section/specific-form-header-section/specific-form-header-section.component';
import { RiskAnalysisComponent } from '../../components/risk-analysis/risk-analysis.component';
import { DisplayAdvice, TgaCropFieldWithStatus, TranslationParams } from '../../shared/models/models';
import { TgaSharedService } from '../../shared/services/tga-shared.service';
import { fetchPlantingAdvice, loadCropFields } from '../../store/tga.actions';
import {
  selectCropFieldById,
  selectCropYearAndCropIdForPlantingAdvice,
  selectPlantingAdvice,
  selectPlantingAdviceTranslationParams,
  getTgaCropYear,
} from '../../store/tga.selectors';

@Component({
  selector: 'app-tga-planting-advice',
  templateUrl: './tga-planting-advice.component.html',
  imports: [SpecificFormHeaderSectionComponent, RiskAnalysisComponent, LoadingCardComponent, TranslatePipe],
})
export class TgaPlantingAdviceComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly tgaSharedService = inject(TgaSharedService);

  readonly plantingAdvice = signal<DisplayAdvice[]>([]);
  cropFieldId?: number;
  cropYear = new Date().getFullYear();
  readonly cropField = signal<CropField | null>(null);
  readonly isLoading = signal(true);
  readonly translationParams = signal<TranslationParams>({});

  private readonly destroy$ = new Subject<void>();
  private readonly params$ = this.route.params;
  private readonly plantingAdvice$ = this.store.select(selectPlantingAdvice);
  private readonly cropYearAndCropId$ = this.store.select(selectCropYearAndCropIdForPlantingAdvice);
  private readonly translationParams$ = this.store.select(selectPlantingAdviceTranslationParams);

  ngOnInit(): void {
    this.store
      .select(getTgaCropYear)
      .pipe(take(1))
      .subscribe((cropYear) => {
        this.cropYear = cropYear;
        this.initializeParams();
        this.createCropFieldStream().pipe(takeUntil(this.destroy$)).subscribe();
        this.translationParams$.pipe(takeUntil(this.destroy$)).subscribe((params) => {
          this.translationParams.set(params);
        });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeParams(): void {
    this.params$
      .pipe(
        tap((params: { cropFieldId?: string | number; cropYear?: string | number }) => {
          if (params.cropFieldId != null) this.cropFieldId = Number(params.cropFieldId);
        }),
        switchMap((params) => this.store.select(selectCropFieldById(Number(params.cropFieldId)))),
        tap((cropField) => this.handleCropFieldResult(cropField)),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.handlePlantingAdviceLoading();
  }

  private handlePlantingAdviceLoading(): void {
    combineLatest([this.params$, this.plantingAdvice$, this.cropYearAndCropId$])
      .pipe(
        tap(([params, plantingAdvice, stateInfo]) => {
          const currentCropFieldId = Number(params.cropFieldId);
          const currentCropYear = this.cropYear;

          const needsToFetchAdvice =
            !plantingAdvice || stateInfo.cropFieldId !== currentCropFieldId || stateInfo.cropYear !== currentCropYear;

          if (needsToFetchAdvice && currentCropFieldId) {
            this.isLoading.set(true);
            this.store.dispatch(
              fetchPlantingAdvice({
                cropFieldId: currentCropFieldId,
                cropYear: currentCropYear,
              }),
            );
          } else if (plantingAdvice) {
            this.plantingAdvice.set(plantingAdvice.data!);
            this.isLoading.set(false);
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private createCropFieldStream() {
    return this.params$.pipe(
      switchMap((params) => this.store.select(selectCropFieldById(Number(params.cropFieldId)))),
      tap((cropField) => this.handleCropFieldResult(cropField)),
    );
  }

  private handleCropFieldResult(cropFieldWithStatus: TgaCropFieldWithStatus | null): void {
    if (!cropFieldWithStatus) {
      this.store.dispatch(loadCropFields({ cropYear: this.cropYear }));
      return;
    }

    this.cropField.set(cropFieldWithStatus.cropField);

    if (!cropFieldWithStatus.cropField.cropId) {
      return;
    }
  }

  getEndDateYear(cropField?: CropField): number | null {
    const field = cropField ?? this.cropField();
    if (!field) return this.cropYear;
    return this.tgaSharedService.getEndDateYear(field, this.cropYear);
  }
}
